import { CommonActions } from '@bugbug/core/actions/actions';
import { isFinishedStatus } from '@bugbug/core/types/base';
import { mergeDeepIncludingArraysElements } from '@bugbug/core/utils/toolbox';
import * as sentry from '@sentry/react';

import type { MiddlewareAPI } from 'redux';

import type { PlaybackCursorPosition } from '@bugbug/core/types/playback';
import type { Test, TestRun } from '@bugbug/core/types/tests';
import analytics from '~/services/analytics';
import { getRelatedTest, isTriggeredByCurrentUser } from '~/utils/runs';

import { ONBOARDING_STEP_ORDER } from '../constans';
import { dispatchInExtension } from '../extension/extension.dispatch';
import { schedulesApi } from '../schedules/schedules.api';
import { TestRunActions } from '../testRun/testRun.redux';
import { selectPlaybackCursorPosition } from '../uiState/uiState.selectors';
import { selectCurrentOnboardingStep, selectUserId } from '../user/user.selectors';
import { createExternalActionsListener } from '../websocket/backend.utils';

import {
  addStepsToCurrentTestGroup,
  invalidateTestsList,
  removeStepsFromCurrentTest,
  updateCurrentTestQuery,
  updateNormalizedGroupInCurrentTest,
  updateTestsListQuery,
} from './tests.api';
import {
  selectCurrentTest,
  selectCurrentTestIsRecording,
  selectSortedSteps,
} from './tests.selectors';
import { normalizeTest } from './tests.utils';

export const testsExternalActionsListener = createExternalActionsListener({
  backendActions: {
    testUpdated: async (action, { getState, dispatch }) => {
      const { testId, steps, group } = action.payload;

      updateCurrentTestQuery(getState, dispatch, (draft) => {
        if (draft.test.id !== testId) return;

        // Add new group
        if (!draft.groups[group.id]) {
          draft.test.groups.push(group.id);
          draft.groups[group.id] = { ...group, steps: [] };
        }

        // Manage steps
        removeStepsFromCurrentTest(draft, { ids: steps.removed });
        addStepsToCurrentTestGroup(draft, {
          groupId: group.id,
          steps: steps.added,
        });
        // eslint-disable-next-line no-restricted-syntax
        for (const modifiedStep of steps.modified) {
          if (modifiedStep.id) {
            draft.steps[modifiedStep.id] = mergeDeepIncludingArraysElements(
              draft.steps[modifiedStep.id] ?? {},
              modifiedStep,
            );
          }
        }

        // Manage group
        updateNormalizedGroupInCurrentTest(draft, { group });
      });

      dispatch(invalidateTestsList());
    },
    setRecordingState: async (action, { getState, dispatch }) => {
      const test = action.payload;

      const isRecording = selectCurrentTestIsRecording(getState());

      if (!isRecording) {
        analytics.trackEvent('recording_stopped');
      }

      const onboardingStep = selectCurrentOnboardingStep(getState());
      const stepIndex = ONBOARDING_STEP_ORDER.indexOf(onboardingStep);
      if (stepIndex < ONBOARDING_STEP_ORDER.indexOf('firstTestRun')) {
        dispatch(schedulesApi.util.invalidateTags(['Schedules']));
      }

      updateCurrentTestQuery(getState, dispatch, (draft) => {
        if (draft.test?.id === test.id) {
          const currentTestRun = draft.test.testRun;
          const normalizedTest = normalizeTest(test);
          Object.assign(draft, normalizedTest);

          draft.test.testRun = currentTestRun;
        }
      });
      dispatch(CommonActions.setIsSingleLoading({ testId: test.id, isLoading: false }));
    },

    newStepsSet: async (action, { getState, dispatch }) => {
      const test = action.payload;

      updateCurrentTestQuery(getState, dispatch, (draft) => {
        if (draft.test?.id === test.id) {
          const normalizedTest = normalizeTest(test);
          Object.assign(draft, normalizedTest);
        }
      });
    },

    testRunStopped: async (action, { getState, dispatch }) =>
      testsStopped(
        {
          results: [action.payload],
        },
        { getState, dispatch },
      ),

    testRunStoppedMultiple: async (action, { getState, dispatch }) =>
      testsStopped(
        {
          results: action.payload,
        },
        { getState, dispatch },
      ),

    stepElementScreenshotRecorded: async (action, { getState, dispatch }) => {
      const { stepId, screenshot } = action.payload;
      updateCurrentTestQuery(getState, dispatch, (draft) => {
        draft.elementsScreenshots[stepId] = screenshot;
      });
    },

    stepElementScreenshotUpdated: async (action, { getState, dispatch }) => {
      const { stepId, screenshot } = action.payload;
      updateCurrentTestQuery(getState, dispatch, (draft) => {
        draft.elementsScreenshots[stepId] = screenshot;
      });

      dispatch(TestRunActions.stepElementScreenshotUpdated(action.payload));
    },

    testRunUpdated: async (action, { getState, dispatch }) =>
      testRunUpdated(
        {
          results: [action.payload],
        },
        { getState, dispatch },
      ),

    testRunUpdatedMultiple: async (action, { getState, dispatch }) =>
      testRunUpdated(
        {
          results: action.payload,
        },
        { getState, dispatch },
      ),
  },
  commonActions: {
    keepLatestPlaybackCursorPosition: async (action, { getState, dispatch }) => {
      const cursorStepIndex = action.payload;
      const updatedSortedSteps = selectSortedSteps(getState());

      const reorderedAfterStep = updatedSortedSteps[cursorStepIndex];
      const reorderedNextStep = updatedSortedSteps[cursorStepIndex + 1];

      const position = {
        afterStep: reorderedAfterStep
          ? {
              id: reorderedAfterStep.id,
              groupId: reorderedAfterStep.groupId,
            }
          : null,
        runningStep: null,
        nextStep: reorderedNextStep
          ? {
              id: reorderedNextStep.id,
              groupId: reorderedNextStep.groupId,
            }
          : null,
      };

      dispatch(CommonActions.setPlaybackCursorPosition(position));
      dispatchInExtension(CommonActions.setPlaybackCursorPositionIfPausedOrIdle(position));
    },
    refreshPlaybackCursorPosition: async (action, { getState, dispatch }) => {
      const step = action.payload?.step ?? selectPlaybackCursorPosition(getState())?.afterStep;
      if (step) {
        setPlaybackCursorPositionAfterStep(step, { getState, dispatch });
      }
    },
  },
});

// Helpers
const testRunUpdated = (
  { results = [] }: { results: TestRun[] },
  { getState, dispatch }: MiddlewareAPI,
) => {
  const currentUserId = selectUserId(getState());

  updateTestsListQuery(getState, dispatch, (draft) => {
    if (!draft?.length) return;

    for (let index = 0; index < results.length; index += 1) {
      const testRun = results[index];
      const testId = getRelatedTest(testRun)?.id;

      // If test is on the list
      const testIndex = draft.findIndex((test) => test.id === testId);

      if (draft[testIndex]) {
        const newResult: Test['lastResult'] = {
          id: testRun.id,
          status: testRun.status,
          started: testRun.started,
          sequence: testRun.sequence,
          user: testRun.user?.id,
        };

        if (isTriggeredByCurrentUser(results[index], currentUserId)) {
          const lastUnfinishedUserRunSequence =
            draft[testIndex].lastUnfinishedUserRun?.sequence ?? 0;

          if (testRun.sequence >= lastUnfinishedUserRunSequence) {
            if (isFinishedStatus(newResult?.status)) {
              draft[testIndex].lastResult = newResult;
              draft[testIndex].lastUnfinishedUserRun = null;
            } else {
              draft[testIndex].lastUnfinishedUserRun = newResult;
            }
          }
        }

        const lastResultSequence = draft[testIndex].lastResult?.sequence ?? 0;
        if (newResult.sequence >= lastResultSequence && isFinishedStatus(newResult.status)) {
          draft[testIndex].lastResult = newResult;
        }
      }
    }
  });

  updateCurrentTestQuery(getState, dispatch, (draft) => {
    const resultForCurrentTest = results.find(
      (result) => getRelatedTest(result)?.id === draft.test.id,
    );
    if (resultForCurrentTest && draft.test.testRun) {
      draft.test.testRun.status = resultForCurrentTest.status;
    }
  });
};

const testsStopped = (
  { results }: { results?: { testId: Test['id']; testRunId: TestRun['id'] }[] },
  { getState, dispatch }: MiddlewareAPI,
) => {
  updateTestsListQuery(getState, dispatch, (draft) => {
    if (!draft?.length) return;

    const testsIds = results?.map((result) => result.testId) || draft.map((test) => test.id);
    for (let index = 0; index < testsIds.length; index += 1) {
      const testId = testsIds[index];

      const testIndex = draft.findIndex((test) => test.id === testId);
      if (testIndex > -1 && draft[testIndex].lastResult) {
        draft[testIndex].lastResult = {
          ...draft[testIndex].lastResult,
          status: 'stopped',
        };
      }
    }
  });
};

const setPlaybackCursorPositionAfterStep = (
  step: PlaybackCursorPosition['afterStep'] = null,
  { getState, dispatch }: MiddlewareAPI,
) => {
  const state = getState();
  const sortedSteps = selectSortedSteps(state);
  const currentTest = selectCurrentTest(state);

  if (!currentTest) return;

  if (!step) {
    const position = {
      afterStep: {
        id: null,
        groupId: sortedSteps[0]?.groupId,
      },
      runningStep: null,
      nextStep: {
        id: sortedSteps[0]?.id,
        groupId: sortedSteps[0]?.groupId,
      },
    };

    dispatch(CommonActions.setPlaybackCursorPosition(position));
    dispatchInExtension(CommonActions.setPlaybackCursorPositionIfPausedOrIdle(position));
    return;
  }

  const groupIndex = currentTest.groups.indexOf(step.groupId);

  /*
    TODO: It could be avoided by using a different approach to store playback cursor position, eg. storing by testId
    Related issue: DEV-4299
  */
  if (groupIndex === -1) return;

  const stepIndex = sortedSteps.findIndex((s) => s.id === step.id && s.groupId === step.groupId);
  const updatedStepPosition = sortedSteps[stepIndex];

  /*
    After above fix, this should never happen but... happens sometimes.
    Capturing extra data in Sentry was added only for debugging purposes and should be removed after fix.

    Related issue: DEV-4299
  */
  if (!updatedStepPosition) {
    sentry.captureMessage(
      `Created/Updated step is missing while setting playback cursor position`,
      {
        level: 'debug',
        extra: {
          stepId: step.id,
          groupId: step.groupId,
          stepIndex,
          testId: currentTest.id,
          testGroupsList: currentTest.groups,
          testStepsList: sortedSteps.map((s) => `${s.id} (${s.groupId})`),
        },
      },
    );
    return;
  }

  const lastStepBeforeCursor = step.id
    ? step
    : sortedSteps.findLast((s) => currentTest.groups.indexOf(s.groupId) < groupIndex);
  const lastStepIndexBeforeCursor = sortedSteps.findIndex(
    (s) => s.id === lastStepBeforeCursor?.id && s.groupId === lastStepBeforeCursor?.groupId,
  );
  const nextActiveStep =
    sortedSteps.slice(lastStepIndexBeforeCursor + 1).find((s) => s.isActive) ?? null;

  const position = {
    afterStep: step?.id
      ? {
          id: updatedStepPosition.id,
          groupId: updatedStepPosition.groupId,
        }
      : {
          id: null,
          groupId: step.groupId,
        },
    runningStep: null,
    nextStep: nextActiveStep
      ? {
          id: nextActiveStep.id,
          groupId: nextActiveStep.groupId,
        }
      : null,
  };

  dispatch(CommonActions.setPlaybackCursorPosition(position));
  dispatchInExtension(CommonActions.setPlaybackCursorPositionIfPausedOrIdle(position));
};
